import React from 'react';

import type { ChangeWithoutCount } from '@aline/common';
import { Queries } from '@aline/common';
import { Typography } from '@mui/material';
import { getTypographyForSx } from 'src/theme';
import { suggestTextStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  text: {
    ...getTypographyForSx(theme.typography.body1),
  },
  textDiffDeletion: {
    ...getTypographyForSx(theme.typography.body1),
    ...suggestTextStyles(theme.appColors, 'remove'),
  },
  textDiffInsertion: {
    ...getTypographyForSx(theme.typography.body1),
    // Diff insertions read as plain blue text with no underline, so this takes
    // only the color from the shared insert treatment.
    color: theme.appColors.suggestInsert,
  },
}));

interface DiffViewProps extends React.HTMLAttributes<HTMLDivElement> {
  changes: ChangeWithoutCount[];
  dataCy?: string;
}

export function DiffView({ changes, dataCy, ...rest }: DiffViewProps) {
  const alineChanges = Queries.batchChangesByNewLine(changes);
  const changesInBatches = alineChanges.reduce<ChangeWithoutCount[][]>((acc, alineChange) => {
    if (Queries.getIsBreakChange(alineChange)) {
      acc.push([]);
      return acc;
    }

    const lastBatch = acc.at(-1);
    if (lastBatch) {
      lastBatch.push(alineChange);
    } else {
      acc.push([alineChange]);
    }
    return acc;
  }, []);

  return (
    <div data-cy={dataCy} {...rest}>
      {changesInBatches.map((changes, batchIndex) => {
        return (
          <div key={batchIndex}>
            <Changes changes={changes} />
          </div>
        );
      })}
    </div>
  );
}

interface ChangesProps {
  changes: ChangeWithoutCount[];
}

function Changes({ changes }: ChangesProps): JSX.Element {
  const { classes } = useStyles();

  // Use zero-width character if paragraph has empty string.  See - https://stackoverflow.com/questions/8692041/minimum-height-for-div-or-span-with-empty-element.
  if (!changes.length) return <Typography>{'\u200b'}</Typography>;

  return (
    <>
      {changes.map((change, changeIndex) => {
        const { added, removed, value } = change;
        const className = added
          ? classes.textDiffInsertion
          : removed
            ? classes.textDiffDeletion
            : classes.text;

        // Use <span /> so changes in a batch all render inline w/ no weird breaks.
        return (
          <span key={changeIndex} className={className}>
            {value || '\u200b'}
          </span>
        );
      })}
    </>
  );
}
